import { Link } from "react-router-dom";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { Callout } from "@/components/ui/feedback";
import { CopyButton } from "@/components/ui/misc";
import { apiUrl } from "@/lib/api";
import type { Trigger } from "@/lib/types";
import {
  BASIC_USER, EMAIL_RECEIVED, MESSAGE_RECEIVED, MESSAGE_SEND, MESSAGE_STATUS, TWILIO_RETRY, absoluteUrl, exampleBody, formFieldNames,
  shellQuote, urlWithSecret,
} from "@/lib/triggers";

/**
 * How to call a trigger (ADR-021): the URL, where the secret goes, a body its mapping reads, and the
 * steps in a zap, or in Twilio for a text trigger and for a delivery-report trigger (ADR-095 and
 * ADR-098 in platform). The body is built from the trigger's own mapping, so it is always one the
 * trigger can apply; the secret is never here, because core-api cannot read it back.
 *
 * A trigger whose secret travels as basic credentials is called at a URL with the secret in it, so
 * the URL is shown with a placeholder where the secret goes.
 *
 * An email trigger (ADR-180 in platform) is posted to by SendGrid's Inbound Parse, at its own route,
 * with the email as SendGrid parses it: no body here would be one, so it gets the SendGrid steps and
 * no example.
 */
export function TriggerSetupCard({ trigger }: { trigger: Trigger }) {
  const basic = trigger.secret_kind === "basic";
  const texts = trigger.action === MESSAGE_RECEIVED;
  const reports = trigger.action === MESSAGE_STATUS;
  const emails = trigger.action === EMAIL_RECEIVED;
  const plain = absoluteUrl(apiUrl(trigger.path), window.location.origin);
  const url = basic ? urlWithSecret(plain, "<the secret>") : plain;
  const body = exampleBody(trigger);
  const names = formFieldNames(trigger);
  const json = JSON.stringify(body, null, 2);
  // A provider posts a form, so a text or a report trigger's example does too, when its paths are
  // plain names.
  const asForm = (texts || reports) && names !== null;
  const curl = [
    `curl -X POST ${shellQuote(plain)} \\`,
    basic
      ? `  -u ${shellQuote(`${BASIC_USER}:<the secret>`)} \\`
      : `  -H ${shellQuote(`${trigger.secret_header}: <the secret>`)} \\`,
    ...(asForm
      ? Object.entries(body).map(
          ([name, value], index, all) =>
            `  --data-urlencode ${shellQuote(`${name}=${String(value)}`)}${index < all.length - 1 ? " \\" : ""}`,
        )
      : [`  -H 'Content-Type: application/json' \\`, `  -d ${shellQuote(JSON.stringify(body))}`]),
  ].join("\n");

  return (
    <Card className="overflow-hidden">
      <CardHeader
        title="How to call it"
        description={
          texts
            ? "Where the provider posts each text, and how to point Twilio at it."
            : emails
              ? "Where SendGrid posts each email sent to a reply address of ours, and how to point it here."
              : reports
              ? "Where the provider reports what became of each text we sent, and how Twilio is told."
              : "Where a sender posts, what it sends, and how to set a zap up."
        }
      />
      <CardBody className="space-y-4">
        <dl className="grid grid-cols-[6rem_1fr] items-center gap-x-3 gap-y-2 text-xs">
          <dt className="text-text-muted">URL</dt>
          <dd className="flex min-w-0 items-center gap-1 font-mono text-text">
            <span className="break-all">{url}</span>
            <CopyButton value={url} label="Copy the URL" />
          </dd>
          <dt className="text-text-muted">Method</dt>
          <dd className="font-mono text-text">POST</dd>
          {basic ? (
            <>
              <dt className="text-text-muted">Secret</dt>
              <dd className="text-text">
                In the URL, in place of <span className="font-mono">&lt;the secret&gt;</span>: the one shown when the
                trigger was made, or last rotated. The name before it can be anything.
              </dd>
            </>
          ) : (
            <>
              <dt className="text-text-muted">Header</dt>
              <dd className="flex items-center gap-1 text-text">
                <span className="font-mono">{trigger.secret_header}</span>
                <CopyButton value={trigger.secret_header} label="Copy the header name" />
                <span className="text-text-muted">with the secret shown when the trigger was made, or last rotated</span>
              </dd>
            </>
          )}
        </dl>

        {trigger.action === "capture" && (
          <Callout tone="info">
            This trigger only keeps what arrives. Send it a request, open the request below to see its fields, then
            change the trigger's action and map them.
          </Callout>
        )}

        {trigger.action === MESSAGE_SEND && (
          <Callout tone="info">
            The same request twice is one text. To say the same words to the same person again, send an{" "}
            <span className="font-mono">Idempotency-Key</span> header with each request, or have the trigger know a
            request by an id of the sender's own. A text asked for outside 8 am to 9 pm on the person's clock is kept
            and goes out at their 8 am; one that may not leave at all is refused. Requests below says which, and when.
          </Callout>
        )}

        {emails ? (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-text">From SendGrid</p>
            <ol className="list-decimal space-y-1 pl-5 text-xs text-text-muted">
              <li>
                The <EmailSettingsLink /> make this trigger and show its URL whole, the secret in it, ready to
                paste.
              </li>
              <li>
                In SendGrid: Settings, Inbound Parse, the reply domain as the receiving domain and that URL as the
                destination. Leave "POST the raw, full MIME message" off, and turn "Check incoming emails for spam"
                on.
              </li>
              <li>
                Reply to an email sent from a contact's record. The request shows up under Requests below, with the
                thread it joined.
              </li>
            </ol>
            {!basic && (
              <Callout tone="warning">
                SendGrid sends the secret only in the URL it posts to. Edit the trigger and let the secret travel in
                the URL.
              </Callout>
            )}
          </div>
        ) : reports ? (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-text">From Twilio</p>
            <ol className="list-decimal space-y-1 pl-5 text-xs text-text-muted">
              <li>
                The <SmsSettingsLink /> make the report trigger for Twilio themselves, and send its URL to Twilio
                with each text. Nothing is pasted anywhere, and a report trigger made by hand is not the one
                they send.
              </li>
              <li>
                Only when nothing else sends through the Messaging Service, a trigger made by hand can be set
                there: Messaging, Services, the service, Integration, the delivery status callback, with the URL
                above, the secret in it and <span className="font-mono">{TWILIO_RETRY}</span> at its end.
              </li>
              <li>
                Send a text from a conversation. Its reports show up under Requests below, and the text becomes
                Delivered.
              </li>
            </ol>
            {!basic && (
              <Callout tone="warning">
                Twilio cannot send a header of its own. Edit the trigger and let the secret travel in the URL.
              </Callout>
            )}
          </div>
        ) : texts ? (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-text">From Twilio</p>
            <p className="text-xs text-text-muted">
              The <SmsSettingsLink /> make this trigger for Twilio and show its URL whole, ready to paste. For one
              made by hand:
            </p>
            <ol className="list-decimal space-y-1 pl-5 text-xs text-text-muted">
              <li>
                One number: Phone Numbers, the number, Messaging configuration, "A message comes in". Every number
                of a Messaging Service: Messaging, Services, the service, Integration, "Send a webhook".
              </li>
              <li>Webhook, HTTP POST, with the URL above and the secret in it.</li>
              <li>
                Add <span className="font-mono">{TWILIO_RETRY}</span> to the end of the URL. Twilio then sends a
                text again when we could not take it. The same text twice is kept once.
              </li>
              <li>
                Text the number. The request shows up under Requests below with the inbox it went to, which is
                the one the number's route names on Numbers.
              </li>
            </ol>
            {!basic && (
              <Callout tone="warning">
                Twilio cannot send a header of its own. Edit the trigger and let the secret travel in the URL.
              </Callout>
            )}
          </div>
        ) : (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-text">From a zap</p>
            <ol className="list-decimal space-y-1 pl-5 text-xs text-text-muted">
              <li>Add an action step: Webhooks by Zapier, then POST.</li>
              <li>URL: the URL above.</li>
              <li>
                {names
                  ? `Payload type: Form or JSON. Data: one field for each of ${names.join(", ")}, each filled from an earlier step.`
                  : "Payload type: JSON, with Unflatten on. Data: a field for each path below, with two underscores where the path has a dot (contact__phone for contact.phone)."}
              </li>
              <li>
                {basic
                  ? "Basic Auth: any user name, and the secret as the password."
                  : `Headers: ${trigger.secret_header}, with the secret as its value.`}
              </li>
              <li>Test the step. The request shows up under Requests below, with what it made.</li>
            </ol>
          </div>
        )}

        {!emails && (
          <>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-medium text-text">{asForm ? "The fields it reads" : "A body it reads"}</p>
                <CopyButton value={json} label="Copy the body" />
              </div>
              <pre className="max-h-60 overflow-auto rounded-md border border-border bg-surface-sunken px-3 py-2 font-mono text-2xs text-text">
                {Object.keys(body).length > 0 ? json : "Any JSON object, or any form."}
              </pre>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-medium text-text">From a terminal</p>
                <CopyButton value={curl} label="Copy the command" />
              </div>
              <pre className="overflow-x-auto rounded-md border border-border bg-surface-sunken px-3 py-2 font-mono text-2xs text-text">
                {curl}
              </pre>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

/** The page that sets texting up, and makes the two Twilio triggers itself (ADR-099 in platform). */
function SmsSettingsLink() {
  return (
    <Link to="/sms" className="underline hover:text-text">
      SMS settings
    </Link>
  );
}

function EmailSettingsLink() {
  return (
    <Link to="/email" className="underline hover:text-text">
      Email settings
    </Link>
  );
}
